import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'
import {
  Button,
  Checkbox,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import useStyles from 'components/Table/useTableStyles'
import usePatientSearch from 'features/patient/usePatientSearch'
import * as storage from 'api/storageApi'
import useToolbarStyles, {
  useOutlinedInputStyles,
} from 'components/Table/useTableToolbarStyles'
import {Features} from 'Authority'
import {PatientSearchRequest} from 'api/patientApi'
import {getQueryFromUrl} from 'helpers/commonHelper'
import SearchBar from 'components/Forms/SearchBar'
import Pagination from 'components/molcules/Pagination'
import {ClientName} from 'components/atoms/ClientName'
import {makeDateAndTimeFormat, toLocalDate} from 'helpers/dateHelper'
import {history} from 'AppHistory'

const SEARCH_SELECT_ITEMS: SelectItem<PatientSearchKind>[] = [
  {label: 'IChartNo', value: 'CHART_NO'},
  {label: 'ICustomerName', value: 'NAME'},
  {label: 'IBirthday', value: 'BIRTH'},
]

const SEARCH_SELECT_ITEMS_WHITE_LIST: SelectItem<PatientSearchKind>[] = [
  {label: 'IChartNo', value: 'CHART_NO'},
  {label: 'IRegistrationNumber', value: 'VISIT_NO'},
  {label: 'ICustomerName', value: 'NAME'},
  {label: 'IBirthday', value: 'BIRTH'},
]

const HEAD_CELLS_WHITE_LIST: HeadCell[] = [
  {id: 'chartNo', label: 'IMedicalRecordNumber'},
  {id: 'visitNo', label: 'IRegistrationNumber'},
  {id: 'name', label: 'ICustomerName'},
  {id: 'birth', label: 'IBirthday'},
  {id: 'gender', label: 'IGender'},
  {id: 'latestDate', label: 'ILastScanDate'},
  {id: 'primary_name', label: 'IManager'},
]

const HEAD_CELLS: HeadCell[] = [
  {id: 'chartNo', label: 'IMedicalRecordNumber'},
  {id: 'name', label: 'ICustomerName'},
  {id: 'birth', label: 'IBirthday'},
  {id: 'gender', label: 'IGender'},
  {id: 'latestDate', label: 'ILastScanDate'},
  {id: 'primary_name', label: 'IManager'},
]

interface PatientTableProps {
  isWhiteList: boolean
  onSelectPatient: (patient: Patient | null) => void
  selectedPatient: Patient | null
  hasPatientError: boolean
}

interface EnhancedTableToolbarProps {
  isEnableSort: boolean
  isWhiteList: boolean
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>
  isWhiteList: boolean
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const location = useLocation()
  const history = useHistory()
  const {t} = useTranslation()
  const classes = useToolbarStyles()
  const outlinedInputClasses = useOutlinedInputStyles()
  const {isEnableSort, isWhiteList} = props

  const {query: queryState, onSearch, onSearchWithSort} = usePatientSearch()
  // Refactor onSearch function to save query params to URL
  const newOnSearch = (queryOnSearch: PatientSearchRequest) => {
    try {
      const params = new URLSearchParams(
        Object.entries(queryOnSearch).map(([key, value]) => [
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        ]),
      )
      history.replace({pathname: location.pathname, search: params.toString()})
    } catch (error) {
      console.error('Update URLSearchParams failed', error)
    }
    return isEnableSort
      ? onSearchWithSort(queryOnSearch)
      : onSearch(queryOnSearch)
  }
  const [searchCondition, setSearchCondition] =
    React.useState<PatientSearchKind>('CHART_NO')
  const [listSelectItem, setListSelectItem] = React.useState<
    SelectItem<PatientSearchKind>[]
  >([])
  const [searchKeyword, setSearchKeyword] = React.useState('')

  const handleSearchChanged = (event: React.ChangeEvent<{value: unknown}>) =>
    setSearchCondition(event.target.value as PatientSearchKind)

  const handleKeywordSearch = () => {
    const {search, paging: defaultPaging} = queryState
    const {searchName, searchValue} = search

    let paging = {...defaultPaging}
    if (searchName !== searchCondition || searchValue !== searchKeyword) {
      // README: 검색 조건, 키워드 변하면 첫 페이지 부터 다시 검색
      paging = {...defaultPaging, page: 0}
    }
    newOnSearch({
      ...queryState,
      search: {
        searchName: searchCondition,
        searchValue: searchKeyword,
      },
      paging,
    })
  }

  const handleValue = (value: any) => {
    setSearchKeyword(value)
  }

  useEffect(() => {
    const initQuery = getQueryFromUrl(location.search)
    if (initQuery?.searchName || initQuery?.search?.searchName) {
      setSearchCondition(initQuery?.searchName || initQuery?.search?.searchName)
    }
    newOnSearch({
      ...queryState,
      paging: {page: 0, size: 10},
      ...initQuery,
    })
  }, [])

  useEffect(() => {
    if (isWhiteList) {
      setListSelectItem([...SEARCH_SELECT_ITEMS_WHITE_LIST])
    } else {
      setListSelectItem([...SEARCH_SELECT_ITEMS])
    }
  }, [isWhiteList])

  return (
    <div className={classes.root}>
      <div className={classes.searchContainer}>
        <FormControl variant='outlined' className={classes.searchSelect}>
          <Select
            value={searchCondition}
            onChange={handleSearchChanged}
            input={
              <OutlinedInput
                name='age'
                id='outlined-age-simple'
                classes={outlinedInputClasses}
              />
            }
          >
            {listSelectItem.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {t(item.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SearchBar
          onChangeValue={handleValue}
          onSearchEvent={handleKeywordSearch}
          disabledValue={false}
        />
      </div>
    </div>
  )
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {t} = useTranslation()
  const {classes, isWhiteList} = props

  const [headCells, setHeadCells] = useState<HeadCell[]>([])

  useEffect(() => {
    if (isWhiteList) {
      setHeadCells([...HEAD_CELLS_WHITE_LIST])
    } else {
      setHeadCells([...HEAD_CELLS])
    }
  }, [isWhiteList])

  return (
    <TableHead>
      <TableRow className={classes.tableHeader}>
        <TableCell align='center' padding='none' />
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align='center' padding='none'>
            {t(headCell.label)}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const PatientTable = (props: PatientTableProps) => {
  const {isWhiteList, onSelectPatient, selectedPatient, hasPatientError} = props
  const location = useLocation()
  const classes = useStyles()
  const {
    patients: patientList,
    loading: patientLoading,
    query: queryState,
    paging: pagingState,
    pagingInfo,
    onSearch,
    onSearchWithSort,
  } = usePatientSearch()
  const {t} = useTranslation()
  const features = JSON.parse(storage.getFeatures() ?? '')
  const isEnableSortName = features.includes(Features.PATIENT_USING_VISIT)
  const emptyRows = 5 - patientList.length
  const initQuery = getQueryFromUrl(location.search)

  const newOnSearch = (queryOnSearch: PatientSearchRequest) => {
    try {
      const params = new URLSearchParams(
        Object.entries(queryOnSearch).map(([key, value]) => [
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        ]),
      )
      history.replace({pathname: location.pathname, search: params.toString()})
    } catch (error) {
      console.error('Update URLSearchParams failed', error)
    }
    return isEnableSortName
      ? onSearchWithSort(queryOnSearch)
      : onSearch(queryOnSearch)
  }

  const setPageIndex = (page: number) => {
    newOnSearch({
      ...queryState,
      paging: {page, size: queryState.paging.size},
      search: initQuery.search,
    })
  }

  return (
    <TableContainer className={classes.container}>
      <EnhancedTableToolbar
        isEnableSort={isEnableSortName}
        isWhiteList={isWhiteList}
      />
      <Table
        stickyHeader
        className={classes.table}
        aria-labelledby='tableTitle'
        aria-label='enhanced table'
        size='small'
      >
        <EnhancedTableHead classes={classes} isWhiteList={isWhiteList} />
        <TableBody>
          {patientList.map((row, index) => {
            return (
              <TableRow
                hover
                role='checkbox'
                tabIndex={-1}
                key={`${row.uuid}-${index}-${row.name}`}
              >
                <TableCell padding='checkbox'>
                  <Checkbox
                    checked={selectedPatient?.uuid === row.uuid}
                    onChange={() =>
                      onSelectPatient(
                        selectedPatient?.uuid === row.uuid ? null : row,
                      )
                    }
                  />
                </TableCell>
                <TableCell align='center'>{row.chartNo}</TableCell>
                {isWhiteList && (
                  <TableCell align='center'>{row.visitNo}</TableCell>
                )}
                <TableCell align='center'>
                  <ClientName
                    firstName={row.firstName}
                    lastName={row.lastName}
                  />
                </TableCell>
                <TableCell align='center'>{row.birth}</TableCell>
                <TableCell align='center'>{row.gender}</TableCell>
                <TableCell align='center'>
                  {row.latestDate
                    ? makeDateAndTimeFormat(toLocalDate(row.latestDate))
                    : ''}
                </TableCell>
                <TableCell align='center'>
                  {t('IGetFullName', {
                    firstName: row.primaryFirstName ?? '',
                    lastName: row.primaryLastName ?? '',
                  })}
                </TableCell>
              </TableRow>
            )
          })}
          {emptyRows > 0 && (
            <TableRow
              style={{height: 43 * emptyRows, backgroundColor: '#F9F9FB'}}
            >
              <TableCell colSpan={12} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Pagination
        totalPageCount={pagingInfo.totalPages}
        currentPageIndex={pagingState.page}
        itemCountPerPage={pagingState.size}
        setCurrentPageIndex={setPageIndex}
        loading={patientLoading}
        onItemCountPerPageChanged={(event) => {
          newOnSearch({
            ...queryState,
            paging: {
              page: 0,
              size: parseInt(event.target.value as string, 10),
            },
            search: initQuery.search,
          })
        }}
      />
      {hasPatientError && (
        <div
          style={{
            color: '#d03c35',
            fontWeight: 700,
          }}
        >
          * {t('IEdfPatientErrorMessage')}
        </div>
      )}
    </TableContainer>
  )
}

export default PatientTable
